import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import { useState } from "react";
import type { Project } from "@/lib/index";
import { useLangStore } from "@/lib/langStore";
import { translations } from "@/lib/i18n";
import { ROUTE_PATHS } from "@/lib/index";
import { ArrowUpRight } from "lucide-react";

interface ProjectCardProps {
  project: Project;
  index?: number;
}

export function ProjectCard({ project, index = 0 }: ProjectCardProps) {
  const { lang } = useLangStore();
  const t = translations[lang];
  const [activeColor, setActiveColor] = useState<string | null>(null);

  const title = lang === "ar" ? project.titleAr : project.title;
  const description = lang === "ar" ? project.descriptionAr : project.description;
  const tags = lang === "ar" ? project.tagsAr : project.tags;
  const catLabel =
    project.category === "brand"
      ? t.portfolio.filterBrand
      : project.category === "corporate"
      ? t.portfolio.filterCorporate
      : t.portfolio.filterSocial;

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ delay: index * 0.1, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
      whileHover={{ y: -8, scale: 1.02 }}
      className="group relative rounded-2xl border border-border bg-card overflow-hidden cursor-pointer shadow-xl hover:shadow-2xl transition-all duration-300"
      style={{ boxShadow: "0 10px 30px -10px rgba(0,0,0,0.3)" }}
    >
      {/* Logo Display Area */}
      <div 
        className={`relative h-56 flex items-center justify-center overflow-hidden transition-colors duration-500 ${!activeColor ? `bg-gradient-to-br ${project.gradient}` : ""}`}
        style={activeColor ? { backgroundColor: activeColor } : {}}
      >
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-4 right-4 w-16 h-16 rounded-full border border-current" />
          <div className="absolute bottom-4 left-4 w-8 h-8 rounded-full border border-current" />
        </div>

        {/* Logo icon */}
        <motion.div
          className={`select-none flex items-center justify-center relative z-10 ${project.category === "social" ? "w-full h-full" : ""}`}
          whileHover={project.category === "social" ? { scale: 1.05 } : { scale: 1.1, rotate: 5 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
        >
          {project.icon.startsWith("/") ? (
            <img
              src={project.icon}
              alt={title}
              className={`${
                project.category === "social" 
                  ? "w-full h-full object-contain" 
                  : project.icon.includes("work1.jpg") 
                    ? "w-36 h-36 object-contain rounded-xl border border-white/10" 
                    : "w-44 h-44 object-contain rounded-xl " + (project.icon.includes("work4.jpg") ? "" : "border border-white/10")
              }`}
              style={project.category === "social" ? {} : { filter: `drop-shadow(0 0 25px ${activeColor || project.colors[0]}70)` }}
            />
          ) : (
            <span
              className="text-8xl"
              style={{ filter: `drop-shadow(0 0 25px ${activeColor || project.colors[0]}70)` }}
            >
              {project.icon}
            </span>
          )}
        </motion.div>

        {/* Color dots - Interactive */}
        <div className="absolute bottom-4 right-4 flex gap-1.5 z-20">
          {project.colors.map((color, i) => (
            <button
              key={i}
              onClick={(e) => {
                e.stopPropagation();
                setActiveColor(activeColor === color ? null : color);
              }}
              className={`w-5 h-5 rounded-full border-2 transition-all duration-300 cursor-pointer ${activeColor === color ? "border-white scale-125 shadow-lg" : "border-white/20 hover:scale-110"}`}
              style={{ backgroundColor: color }}
              title="Change background color"
            />
          ))}
        </div>

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/5 transition-colors duration-300 pointer-events-none" />
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div>
            <h3 className="font-semibold text-foreground text-lg leading-tight">{title}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {project.client} · {project.year}
            </p>
          </div>
          <NavLink
            to={ROUTE_PATHS.PORTFOLIO}
            className="shrink-0 w-8 h-8 rounded-lg bg-muted flex items-center justify-center text-muted-foreground group-hover:bg-primary group-hover:text-primary-foreground group-hover:scale-110 group-hover:rotate-12 transition-all duration-300"
          >
            <ArrowUpRight size={14} />
          </NavLink>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 mb-3">{description}</p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5">
          <span className="px-2 py-0.5 rounded-md bg-primary/10 text-primary text-xs font-medium">
            {catLabel}
          </span>
          {tags.slice(0, 2).map((tag) => (
            <span key={tag} className="px-2 py-0.5 rounded-md bg-muted text-muted-foreground text-xs">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
